import {App} from "vue";
import {fileSize, random} from "@/lib/util";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import * as Sentry from "@sentry/vue";
import {openobserveRum} from "@openobserve/browser-rum";
import config from "@/config";
import {openobserveLogs} from "@openobserve/browser-logs";
import {UserAgent} from "@/model/user_agent";
import {Broker} from "@/model/broker";
import {User} from "@openobserve/browser-core";
import {Material} from "@/model/material";
import '@/lib/captcha/aliyun.captcha.js'
import {getInstance} from "@/lib/captcha/aliyun.captcha.js";
import router from "@/views";
import {api} from "@/lib/api";
import emit from "@/lib/emit";
import {message, Modal, UploadFile} from "ant-design-vue";


class Session {
    agent: UserAgent | null = null;
    broker: Broker | null = null;
    readonly isProduction = process.env.NODE_ENV === 'production';

    private _material: Material | null = null;
    private loading = false;
    private countdown = 0
    private interval = 0

    constructor() {
        emit.on('session.timeout', () => this.timeout())
    }

    private timeout(): void {
        console.log('session timeout');
        if (!this.loading) {
            this.loading = true;
            this.countdown = 5
            const m = Modal.confirm({
                title: '会话已过期',
                content: `${this.countdown}秒后刷新页面？`,
                closable: false,
                okCancel: false,
                okText: '立即刷新',
                onOk: () => {
                    this.loading = false;
                    location.reload();
                    clearInterval(this.interval);
                }
            });
            this.interval = window.setInterval(() => {
                this.countdown -= 1
                if (this.countdown == 0) {
                    location.reload();
                    clearInterval(this.interval);
                } else m.update({content: `${this.countdown}秒后刷新页面？`})
            }, 1000);
        }
    }

    async begin(app: App) {
        if (this.isProduction) this.initMonitor(app)
        await this.register()
    }

    private async genFingerprint() {
        try {
            const fp = await FingerprintJS.load()
            const res = await fp.get()
            return res.visitorId
        } catch (e) {
            return random(32)
        }
    }

    private initMonitor(app: App) {
        Sentry.init({
            app,
            dsn: "https://9c6e5e2a35226dea4ed3e80c03507ef6@o1122643.ingest.us.sentry.io/4507915455823872",
            integrations: [
                Sentry.browserTracingIntegration({router}),
                Sentry.replayIntegration(),
                Sentry.replayCanvasIntegration()
            ],
            // Tracing
            tracesSampleRate: 1.0, //  Capture 100% of the transactions
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [/^https:\/\/agent\.pms\.guandb\.cn\//],
            // Session Replay
            replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        });
        openobserveRum.init({
            applicationId: config.openObserveOption.applicationId, // required, any string identifying your application
            clientToken: config.openObserveOption.clientToken,
            site: config.openObserveOption.site,
            organizationIdentifier: config.openObserveOption.organizationIdentifier,
            service: config.openObserveOption.service,
            env: process.env.NODE_ENV,
            version: process.env.VERSION,
            trackResources: true,
            trackLongTasks: true,
            trackUserInteractions: true,
            apiVersion: config.openObserveOption.apiVersion,
            insecureHTTP: config.openObserveOption.insecureHTTP,
            defaultPrivacyLevel: 'allow' // 'allow' or 'mask-user-input' or 'mask'. Use one of the 3 values.
        })
        openobserveLogs.init({
            clientToken: config.openObserveOption.clientToken,
            site: config.openObserveOption.site,
            organizationIdentifier: config.openObserveOption.organizationIdentifier,
            service: config.openObserveOption.service,
            env: process.env.NODE_ENV,
            version: process.env.VERSION,
            forwardErrorsToLogs: true,
            insecureHTTP: config.openObserveOption.insecureHTTP,
            apiVersion: config.openObserveOption.apiVersion,
        });

        openobserveRum.startSessionReplayRecording();
    }

    private async register() {
        const sp = new URLSearchParams(location.search.slice(1))
        this.update(
            sp.has('token') ?
                await api({url: 'system/login/' + encodeURIComponent(sp.get('token') as string)}) :
                await api({url: 'browser/' + await this.genFingerprint()})
        )
        // this.update(await api<void, any>({
        //     url: '', method: 'POST', data: {
        //         code: await this.genFingerprint(),
        //         quick_auth_token: new URLSearchParams(location.search.slice(1)).get('token')
        //     }
        // }))
    }

    update(res: any): void {
        const ooUser: User = {id: this.agent?.meta.account.agent_id};
        if (res) {
            ooUser.name = res.account.phone_number;
            ooUser.email = `${res.account.agent_id}@guandb.cn`;
            if (res.account.phone_number) {
                if (this.agent) this.agent.update(res)
                else this.agent = new UserAgent(res);
            }
        } else {
            ooUser.name = '';
            ooUser.email = `guest@guandb.cn`;
            this.agent = null
        }
        openobserveRum.setUser(ooUser);
        this.broker = new Broker()
    }

    async loginQR(): Promise<Blob> {
        return await api({url: 'user/login/applet/qr', responseType: 'blob'})
    }

    async smsCaptcha(captchaVerifyParam: string, pn: string): Promise<any> {
        return api({
            url: 'user/sms/captcha',
            method: 'POST',
            data: {param: captchaVerifyParam, mobile: pn}
        })
    }

    async login(phone_number: string, code: string): Promise<void> {
        this.update(await api({
            url: 'user/login',
            method: 'POST',
            data: {phone_number, code}
        }))
    }

    async material(): Promise<Material> {
        if (this._material) return this._material;
        this._material = await api<void, Material>({url: 'user/material'}).then(res => new Material(res))
        return this._material ? this._material : Promise.reject('no material');
    }

    captcha(gi: getInstance) {
        window.initAliyunCaptcha({
            SceneId: 'l04d75k8',
            prefix: '1lnth7',
            mode: 'popup',
            getInstance: gi,
            element: 'body',
            button: '#captcha',
            language: 'cn'
        })
    }

    async logout(): Promise<void> {
        await api({url: 'user/logout'})
        session.agent = null
    }

    async deleteAccount(): Promise<void> {
        await api({url: 'user', method: 'DELETE'})
        session.agent = null
    }

    /**
     * 生成用于将文件上传到 OSS（对象存储服务）的签名数据对象。
     * @param {UploadFile} f - 上传的文件对象。
     * @param {number} [max] - 允许上传文件的最大尺寸单位：byte。
     * @return {Promise<object>} 承诺会返回一个包含上传所需的密钥和其他信息的对象，如果文件大小超过允许的最大大小，则会返回错误并拒绝。
     */
    async ossSign(f: UploadFile, max?: number): Promise<object> {
        console.log(f.size, max)
        if ((f.size ?? 0) > (max ?? 0)) {
            message.warning(`文件尺寸要小于${fileSize(max ?? 2*1024*1024)}`)
            return Promise.reject('尺寸过大')
        }
        f.uid = `media/temp/${Math.random().toString(36).slice(2)}.${f.name.split('.').pop()}`
        f.url = `https://static.pms.guandb.cn/${f.uid}`
        return await api({url: 'user/oss/sign', method: 'POST', data: {key: f.uid, max}})
            .then(res => ({key: f.uid, ...res}))
    }
}

export const session = new Session();
